/**
 * ReCAPTCHA Context
 * Created: 2024-03-27
 * Updated: 2024-08-14 - Fixed handling of environment variables during server-side rendering
 * Updated: 2024-08-16 - Fixed issue with accessing publicRuntimeConfig
 * Updated: 2024-09-01 - Improved error handling for missing siteKey and improved provider safety
 * Updated: 2024-10-05 - Added debugger to inspect siteKey value
 * Updated: 2024-10-05 - Fixed configuration to use environment variables directly instead of getConfig
 * Updated: 2024-12-19 - Reverted to use getConfig() with publicRuntimeConfig for proper runtime access
 * Updated: 2024-12-19 - FIXED: Now receives siteKey as prop from Server Component for proper runtime env var access
 *
 * Provides global reCAPTCHA v3 functionality for form protection.
 */

'use client'

import React, {createContext, useContext, useEffect, useState, ReactNode} from 'react'

// ReCAPTCHA types
declare global {
  interface Window {
    grecaptcha: {
      ready: (callback: () => void) => void
      execute: (siteKey: string, options: {action: string}) => Promise<string>
    }
  }
}

// Context types
interface ReCaptchaContextType {
  executeRecaptcha: (action: string) => Promise<string | null>
  isLoaded: boolean
}

const ReCaptchaContext = createContext<ReCaptchaContextType | undefined>(undefined)

// Provider props - now accepts siteKey from Server Component
interface ReCaptchaProviderProps {
  children: ReactNode
  siteKey?: string // Made optional with default fallback
}

export function ReCaptchaProvider({children, siteKey}: ReCaptchaProviderProps) {
  const [isLoaded, setIsLoaded] = useState(false)

  // Debug logging to track siteKey value
  console.log('ReCaptchaProvider received siteKey:', siteKey)

  // UseEffect must be called before any conditional returns
  useEffect(() => {
    // Only load reCAPTCHA if we're on client side and have a site key
    if (typeof window === 'undefined' || !siteKey) {
      return
    }

    const loadReCaptcha = () => {
      if (window.grecaptcha) {
        window.grecaptcha.ready(() => {
          setIsLoaded(true)
          console.log('reCAPTCHA loaded successfully with siteKey:', siteKey.substring(0, 10) + '...')
        })
        return
      }

      // Load reCAPTCHA script
      const script = document.createElement('script')
      script.src = `https://www.google.com/recaptcha/api.js?render=${siteKey}`
      script.async = true
      script.defer = true

      script.onload = () => {
        window.grecaptcha.ready(() => {
          setIsLoaded(true)
          console.log('reCAPTCHA script loaded and ready with siteKey:', siteKey.substring(0, 10) + '...')
        })
      }

      script.onerror = () => {
        console.error('Failed to load reCAPTCHA script')
      }

      document.head.appendChild(script)

      return () => {
        if (document.head.contains(script)) {
          document.head.removeChild(script)
        }
      }
    }

    loadReCaptcha()
  }, [siteKey])

  // Only render the provider on the client side if we have a site key
  if (typeof window === 'undefined' || !siteKey) {
    if (!siteKey) {
      console.warn('No reCAPTCHA site key provided to ReCaptchaProvider. Authentication will proceed without reCAPTCHA verification.')
    }

    // Return mock provider for server-side rendering or when no site key
    return (
      <ReCaptchaContext.Provider
        value={{
          executeRecaptcha: async () => null,
          isLoaded: false
        }}>
        {children}
      </ReCaptchaContext.Provider>
    )
  }

  const executeRecaptcha = async (action: string): Promise<string | null> => {
    if (!isLoaded || !window.grecaptcha || !siteKey) {
      console.warn('reCAPTCHA not loaded or no site key available')
      return null
    }

    try {
      const token = await window.grecaptcha.execute(siteKey, {action})
      console.log('reCAPTCHA token generated for action:', action)
      return token
    } catch (error) {
      console.error('Failed to execute reCAPTCHA:', error)
      return null
    }
  }

  return <ReCaptchaContext.Provider value={{executeRecaptcha, isLoaded}}>{children}</ReCaptchaContext.Provider>
}

export function useReCaptcha() {
  const context = useContext(ReCaptchaContext)
  if (context === undefined) {
    throw new Error('useReCaptcha must be used within a ReCaptchaProvider')
  }
  return context
}
